/**
 * A reference to ONE glyph: which icon it is, and which library holds it.
 *
 * This is the answer to the question every icon registry in the app used to
 * answer for itself. Before this module there were FIVE shapes for one fact:
 *
 *   `iconLib?: 'material' | 'bower'`            SourceDescriptor
 *   `icon: BowerIconName` + `materialIcon?`     EntityIconConfig (an override)
 *   `icon: string` + `iconSet?: 'bower'`        command palette / nav / settings
 *   `{ bower } | { material }`                  getAttachmentTileIcon
 *   `{ bowerIcon?, materialIcon? }`             the audit timeline
 *
 * Five shapes meant five ways to get it wrong, and the app got it wrong in the
 * predictable place: `SearchableMultiSelectMenu` branched on which field was
 * populated and painted the Bower arm in the brand ramp while the Material arm
 * stayed flat, so whether a filter chip was branded came down to which library
 * its glyph happened to live in. The same registry entry rendered two ways on
 * two screens.
 *
 * WHY A DISCRIMINATED UNION rather than `{ name: string; lib: GlyphLib }`.
 * Bower's own icon set is a closed, hand-drawn list of 16 marks, and
 * `BowerIconName` has always type-checked it. A flat `name: string` would have
 * traded a compile error for a runtime throw on every Bower entry in every
 * registry. The union keeps the check exactly where it was and adds one for the
 * pairing: `{ lib: 'bower', name: 'trash' }` does not compile, because `trash`
 * is not one of the 16.
 *
 * WHAT THIS IS NOT. It carries no size, no colour and no gradient. Those are
 * decisions of the SURFACE the glyph is rendered on, not of the registry entry,
 * and folding them in here is what would turn one shared type back into five —
 * every consumer needing its own variant of "the entry, but 14px". Render it
 * with `<Glyph {...ref} size gradient />`; `theme/iconGradient.ts` owns which
 * glyphs may take the ramp and why.
 */

import type { BowerIconName } from '../components/ui/BowerIcon';

/** Which icon library holds a glyph. */
export type GlyphLib = 'bower' | 'material';

/**
 * One glyph, and the library that holds it.
 *
 * Spread straight into the renderer: `<Glyph {...ref} size="16px" />`.
 */
export type GlyphRef =
  | { readonly lib: 'bower'; readonly name: BowerIconName }
  | { readonly lib: 'material'; readonly name: string };

/**
 * A glyph from Bower's own SVG set. Type-checked against the 16 real marks.
 *
 * Prefer this over an object literal in a registry: the helper is what makes
 * the `lib` field impossible to forget, and a bare literal with the wrong
 * `lib` is exactly the mistake this module exists to remove.
 */
export function bowerGlyph(name: BowerIconName): GlyphRef {
  return { lib: 'bower', name };
}

/** A glyph from Google Material Symbols — the broad set, names unchecked. */
export function materialGlyph(name: string): GlyphRef {
  return { lib: 'material', name };
}
